import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Modal,
  Table,
  Space,
  Button,
  Tag,
  Descriptions,
  message,
  Divider,
  Typography,
  Card,
  Input,
  Form,
  Popconfirm,
  Radio,
  Select,
} from "antd";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  WalletOutlined,
  BankOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import CurrencyFormat from "../../../../hook/CurrencyFormat";
import DateCustom from "../../../../hook/DateCustom";
import {
  GET_LOAD_BANK,
  HEADERS_EXPORT_BEN,
  POST_BILL_DETAIL,
  POST_CLEAR_APPROVAL,
  POST_CLEAR_PAYMENT_DB2,
} from "../../../API/apiUrls";
import axios from "axios";
import "../../../../assets/styles/tableStyles.css";

const { Title, Text } = Typography;
const { TextArea } = Input;

const ExpenseClearPaymentApproval = ({
  open,
  close,
  dataDefault,
  funcUpdateStatus,
  nameLawyers,
}) => {
  const userId = parseInt(localStorage.getItem("USER_ID"));
  const ROLE_ID = parseInt(localStorage.getItem("ROLE_ID"));

  const [
    currencyFormat,
    currencyFormatComma,
    currencyFormatPoint,
    currencyFormatNoPoint,
  ] = CurrencyFormat();
  const [convertDateThai] = DateCustom();
  const [loading, setLoading] = useState(false);
  const [billData, setBillData] = useState(null);
  const [form] = Form.useForm();

  const [bankData, setBankData] = useState(null);
  const [paymentType, setPaymentType] = useState(null);
  const [selectedBank, setSelectedBank] = useState(null);

  // ============== LOAD DATA ==============
  const loadData = useCallback(async () => {
    if (!dataDefault?.TMBILL) return;
    setLoading(true);

    try {
      const { data } = await axios.post(
        POST_BILL_DETAIL,
        { TMBILL: [dataDefault.TMBILL] },
        { headers: HEADERS_EXPORT_BEN },
      );

      if (Array.isArray(data) && data.length > 0) {
        console.log("Bill Detail Response:", data);
        setBillData(data);
      } else {
        message.warning("ไม่พบข้อมูลรายละเอียด");
        setBillData(null);
      }
    } catch (error) {
      console.error("Error loading bill detail:", error);
      message.error(`เกิดข้อผิดพลาดในการโหลดข้อมูล: ${error.message}`);
      setBillData(null);
    } finally {
      setLoading(false);
    }
  }, [dataDefault?.TMBILL]);

  const loadBank = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(GET_LOAD_BANK, {
        headers: HEADERS_EXPORT_BEN,
      });
      if (Array.isArray(data) && data.length > 0) {
        setBankData(data);
      } else {
        message.warning("ไม่พบข้อมูลธนาคาร");
        setBankData([]);
      }
      setLoading(false);
    } catch (error) {
      message.error(`โหลดข้อมูลธนาคารไม่สำเร็จ: ${error.message}`);
      setBankData([]);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    loadData();
    if (
      open &&
      dataDefault?.TMBILL &&
      dataDefault?.clearStatus === 1 &&
      (ROLE_ID === 1 || ROLE_ID === 6)
    ) {
      loadBank();
    }
  }, [
    open,
    loadData,
    form,
    ROLE_ID,
    dataDefault.TMBILL,
    dataDefault,
    loadBank,
    userId,
  ]);

  // ============== PREPARE TABLE DATA ==============
  const tableData = useMemo(() => {
    if (!billData || !Array.isArray(billData)) return [];

    let flatData = [];
    let rowIndex = 0;

    billData.forEach((loan) => {
      if (loan.workExpenseList && Array.isArray(loan.workExpenseList)) {
        loan.workExpenseList.forEach((expense, index) => {
          flatData.push({
            ...expense,
            CONTNO: loan.CONTNO,
            rowIndex: rowIndex++,
            isFirstInGroup: index === 0,
            groupSize: loan.workExpenseList.length,
          });
        });
      }
    });

    return flatData;
  }, [billData]);

  // ============== CALCULATE TOTALS ==============
  const totals = useMemo(() => {
    if (!tableData || tableData.length === 0) {
      return {
        totalNETPAY: 0,
        totalPAY: 0,
        difference: 0,
      };
    }

    const totalNETPAY = tableData.reduce(
      (sum, item) => sum + (parseFloat(item.NETPAY) || 0),
      0,
    );
    const totalPAY = tableData.reduce(
      (sum, item) => sum + (parseFloat(item.PAY) || 0),
      0,
    );

    return {
      totalNETPAY,
      totalPAY,
      difference: totalPAY - totalNETPAY,
    };
  }, [tableData]);

  // ============== TABLE COLUMNS ==============
  const columns = [
    {
      title: "ลำดับ",
      align: "center",
      width: 70,
      render: (_, __, index) => index + 1,
    },
    {
      title: "สัญญา",
      dataIndex: "CONTNO",
      key: "CONTNO",
      width: 120,
      render: (CONTNO, record) => {
        if (record.isFirstInGroup) {
          return {
            children: <Text strong>{CONTNO || "-"}</Text>,
            props: {
              rowSpan: record.groupSize,
            },
          };
        }
        return {
          props: {
            rowSpan: 0,
          },
        };
      },
    },
    {
      title: "รายการ",
      dataIndex: "description",
      key: "description",
      render: (text) => <>{text || "-"}</>,
    },
    {
      title: "ยอดตั้งเบิก",
      dataIndex: "NETPAY",
      key: "NETPAY",
      width: 120,
      align: "right",
      render: (amount) => (
        <Text strong style={{ color: "#1890ff", fontSize: "14px" }}>
          {currencyFormatPoint(amount)}
        </Text>
      ),
    },
    {
      title: "ยอดเคลียร์",
      dataIndex: "PAY",
      key: "PAY",
      width: 120,
      align: "right",
      render: (amount, record) => {
        const payAmount = parseFloat(amount) || 0;
        return (
          <Text strong style={{ color: "#52c41a", fontSize: "14px" }}>
            {currencyFormatPoint(payAmount)}
          </Text>
        );
      },
    },
    {
      title: "ส่วนต่าง",
      key: "difference",
      width: 100,
      align: "right",
      render: (_, record) => {
        const diff =
          (parseFloat(record.PAY) || 0) - (parseFloat(record.NETPAY) || 0);
        const color = diff === 0 ? "#52c41a" : diff < 0 ? "#ff4d4f" : "#1890ff";

        return (
          <Text style={{ color: color, fontSize: "13px", fontWeight: "500" }}>
            {diff > 0 ? "+" : ""}
            {currencyFormatPoint(diff)}
          </Text>
        );
      },
    },
  ];

  // Function ตรวจสอบแถวสุดท้ายของกลุ่ม
  const isLastInGroup = (record, dataSource) => {
    const currentIndex = dataSource.findIndex((item) => item.id === record.id);
    const nextRecord = dataSource[currentIndex + 1];
    return !nextRecord || nextRecord.CONTNO !== record.CONTNO;
  };

  // เพิ่ม function สำหรับหาหมายเลขกลุ่ม
  const getGroupIndex = (record, dataSource) => {
    const uniqueContracts = [...new Set(dataSource.map((item) => item.CONTNO))];
    return uniqueContracts.indexOf(record.CONTNO);
  };

  // สร้างชุดสีสำหรับแต่ละกลุ่ม
  const getGroupColor = (groupIndex) => {
    const colors = [
      // "#ffffff", // สีขาว
      // "#f0f5ff", // น้ำเงินอ่อน
      // "#fff7e6", // ส้มอ่อน
      "#f6ffed", // เขียวอ่อน
      // "#fff0f6", // ชมพูอ่อน
      "#f9f0ff", // ม่วงอ่อน
      // "#e6fffb", // เขียวมิ้นท์อ่อน
      // "#fffbe6", // เหลืองอ่อน
    ];

    return colors[groupIndex % colors.length];
  };

  // ============== HANDLE APPROVE ==============
  const handleApprove = async (values) => {
    const remark = values.remark || "";

    Modal.confirm({
      title: "ยืนยันการอนุมัติการเคลียร์",
      icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
      content: (
        <div>
          <p>
            <strong>เลขที่อ้างอิง:</strong> {dataDefault?.TMBILL}
          </p>
          <p>
            <strong>ผู้ตั้งเบิก:</strong>{" "}
            {nameLawyers?.find((item) => item.id === dataDefault?.userId)
              ?.NNAME ||
              dataDefault?.USERNAME ||
              "-"}
          </p>
          <p>
            <strong>ยอดตั้งเบิก:</strong>{" "}
            <Text strong style={{ color: "#1890ff" }}>
              {currencyFormatPoint(totals.totalNETPAY)} บาท
            </Text>
          </p>
          <p>
            <strong>ยอดเคลียร์:</strong>{" "}
            <Text strong style={{ color: "#52c41a" }}>
              {currencyFormatPoint(totals.totalPAY)} บาท
            </Text>
          </p>
          <p>
            <strong>ส่วนต่าง:</strong>{" "}
            <Text
              strong
              style={{ color: totals.difference === 0 ? "#52c41a" : "#ff4d4f" }}
            >
              {totals.difference > 0 ? "+" : ""}
              {currencyFormatPoint(totals.difference)} บาท
            </Text>
          </p>
          {remark && (
            <p>
              <strong>หมายเหตุ:</strong> {remark}
            </p>
          )}
          <Divider style={{ margin: "12px 0" }} />
          <p style={{ color: "#52c41a" }}>
            คุณต้องการอนุมัติการเคลียร์เงินนี้ใช่หรือไม่?
          </p>
        </div>
      ),
      okText: "อนุมัติ",
      cancelText: "ยกเลิก",
      okButtonProps: { type: "primary" },
      onOk: async () => {
        await submitApproval(1, remark);
      },
    });
  };

  const handleCreatePayment = async () => {
    setLoading(true);
    console.log("dataDefault", dataDefault);

    try {
      const payload = {
        TMBILL: dataDefault.TMBILL,
        userId: dataDefault.userId, // ทนาย
        CHKFLAG: paymentType, // 1 เงินสด 2 เงินโอน
        BANKBOOKCOD: paymentType === "1" ? "" : selectedBank.value, // รหัสแบงค์ (ถ้าเงินสดให้ "" มา)
        DOCNO: dataDefault.DOCNO,
      };
      console.log("📌 Payload ส่ง API:", payload);
      const result = await postClearPaymentDB2(payload);

      if (result) {
        funcUpdateStatus();
      }
      close();
    } catch (err) {
      console.error("❌ Error saving:", err);
    } finally {
      setLoading(false);
    }
  };
  console.log("dataDefault", dataDefault);

  const postClearPaymentDB2 = async (payload) => {
    console.log("📤 POST_DOC_STATUS with data:", payload);

    try {
      const res = await axios.post(POST_CLEAR_PAYMENT_DB2, payload, {
        headers: HEADERS_EXPORT_BEN,
      });

      const { status, data } = res;

      if (status === 200) {
        console.log("✅ API Response:", data);
        return data;
      }
    } catch (error) {
      const status = error?.response?.status;
      const serverMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message;

      if (status === 404) {
        message.error("ไม่พบปลายทาง (404)");
      } else if (status === 400) {
        message.error(`ข้อมูลไม่ถูกต้อง: ${serverMsg}`);
      } else if (status === 500) {
        message.error("เกิดข้อผิดพลาดจากเซิร์ฟเวอร์ (500)");
      } else {
        message.error(
          `ไม่สามารถส่งข้อมูลได้${status ? ` (${status})` : ""}: ${serverMsg}`,
        );
      }

      console.error("❌ postDocStatus error:", error);
      throw error;
    }
  };

  // ============== SUBMIT APPROVAL ==============
  const submitApproval = async (approvalStatus, remark) => {
    setLoading(true);

    const payload = {
      userId: userId,
      TMBILL: dataDefault.TMBILL,
      clearStatus: approvalStatus,
      payamtMark: remark,
      ALL_CONTNO: dataDefault.ALL_CONTNO,
    };

    console.log("Approval Payload:", payload);

    try {
      const response = await axios.post(POST_CLEAR_APPROVAL, payload, {
        headers: HEADERS_EXPORT_BEN,
      });

      if (response.status === 200 || response.status === 201) {
        message.success("อนุมัติการเคลียร์เงินสำเร็จ");

        if (funcUpdateStatus) {
          funcUpdateStatus();
        }

        handleClose();
      } else {
        throw new Error("Unexpected response status");
      }
    } catch (error) {
      console.error("Error submitting approval:", error);
      const errorMsg = error.response?.data?.message || error.message;
      message.error(`เกิดข้อผิดพลาด: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  // ============== HANDLE CLOSE ==============
  const handleClose = () => {
    close();
  };

  // ============== RENDER STATUS ==============
  const renderClearStatus = (status) => {
    const statusMap = {
      0: { text: "รอดำเนินการ", color: "blue" },
      1: { text: "อนุมัติ", color: "green" },
      2: { text: "ไม่อนุมัติ", color: "red" },
    };

    const { text, color } = statusMap[status] || statusMap[0];
    return <Tag color={color}>{text}</Tag>;
  };
  const handleModalCreate = () => {
    const bankOptions = (bankData || []).map((b) => ({
      value: b.BANKBOOKCOD,
      label: `${b.BANKCOD} | ${b.BOOKNAM} | ${b.BOOKNO}`,
      // เก็บ object ทั้งหมดไว้ใน data
      data: b,
    }));
    return (
      <>
        <Card
          size="small"
          title={
            <Text strong>
              วิธีการชำระเงิน <Text type="danger">*</Text>
            </Text>
          }
          style={{ marginBottom: 16, borderRadius: 8 }}
        >
          <Radio.Group
            value={paymentType}
            onChange={(e) => {
              setPaymentType(e.target.value);
              setSelectedBank(null);
            }}
            style={{ width: "100%" }}
          >
            <Space direction="horizontal" size="large">
              <Radio value="1">
                <Space>
                  <WalletOutlined />
                  เงินสด
                </Space>
              </Radio>
              <Radio value="2">
                <Space>
                  <BankOutlined />
                  โอนเงิน
                </Space>
              </Radio>
              <Radio value="3" disabled>
                <Space>
                  <FileTextOutlined />
                  เช็ค
                </Space>
              </Radio>
            </Space>
          </Radio.Group>
          {paymentType !== "1" && (
            <div style={{ marginTop: 12 }}>
              <Text style={{ display: "block", marginBottom: 6 }}>
                เลือกธนาคาร <Text type="danger">*</Text>
              </Text>
              <Select
                placeholder="เลือกธนาคาร"
                options={bankOptions}
                value={selectedBank?.value}
                onSelect={(value, option) => setSelectedBank(option)} // ✅ ได้ { value, label, data }
                showSearch
                optionFilterProp="label"
                style={{ width: "100%" }}
                size="large"
                loading={!bankData}
                optionRender={(
                  option, // ✅ custom render แสดง 3 บรรทัด
                ) => (
                  <Space direction="vertical" size={0}>
                    <Text strong style={{ fontSize: 13 }}>
                      {option.data.data?.BANKCOD}
                    </Text>
                    <Text style={{ fontSize: 12, color: "#555" }}>
                      {option.data.data?.BOOKNAM}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      เลขที่บัญชี: {option.data.data?.BOOKNO}
                    </Text>
                  </Space>
                )}
              />
            </div>
          )}
        </Card>
      </>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: "#1890ff" }} />
          <span style={{ fontSize: "18px", fontWeight: "bold" }}>
            รายละเอียดการเคลียร์เงินทดรองจ่าย
          </span>
        </Space>
      }
      open={open}
      onCancel={handleClose}
      width={1200}
      footer={null}
      centered
      destroyOnHidden
    >
      {/* ============== HEADER INFO ============== */}
      <Card
        size="small"
        style={{
          marginBottom: 16,
          background: "rgba(0, 57, 96, 0.08)",
          border: "1px solid rgba(0, 57, 96, 0.15)",
        }}
      >
        <Descriptions column={3} size="small">
          <Descriptions.Item label="เลขที่อ้างอิง">
            <Tag color="blue" style={{ fontSize: "14px" }}>
              {dataDefault?.TMBILL || "-"}
            </Tag>
          </Descriptions.Item>

          <Descriptions.Item label="ผู้ตั้งเบิก">
            <Text>
              {nameLawyers?.find((item) => item.id === dataDefault?.userId)
                ?.NNAME ||
                dataDefault?.USERNAME ||
                "-"}
            </Text>
          </Descriptions.Item>

          <Descriptions.Item label="สถานะเบิก">
            {renderClearStatus(dataDefault?.approvalStatus)}
          </Descriptions.Item>

          <Descriptions.Item label="วันที่ขอเบิก">
            <Text>
              {dataDefault?.payamtDate
                ? convertDateThai(dayjs(dataDefault.payamtDate))
                : "-"}
            </Text>
          </Descriptions.Item>

          <Descriptions.Item label="วันที่อนุมัติเบิก">
            <Text>
              {dataDefault?.approvalDate
                ? convertDateThai(dayjs(dataDefault.approvalDate))
                : "-"}
            </Text>
          </Descriptions.Item>

          <Descriptions.Item label="วันที่เคลียร์">
            <Text>
              {dataDefault?.clearDate
                ? convertDateThai(dayjs(dataDefault.clearDate))
                : "-"}
            </Text>
          </Descriptions.Item>

          {dataDefault?.approvalStatus === 1 && (
            <Descriptions.Item label="หมายเหตุ" span={3}>
              <Tag color={totals?.difference === 0 ? "blue" : "red"}>
                {totals?.difference > 0
                  ? "เบิกเพิ่ม"
                  : totals.difference < 0
                    ? "คืนการเงิน"
                    : "ปกติ"}
              </Tag>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* ============== SUMMARY CARDS ============== */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "16px" }}>
        <Card
          size="small"
          style={{
            flex: 1,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            border: "none",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <Text style={{ color: "white", fontSize: "12px" }}>
              ยอดตั้งเบิก
            </Text>
            <div>
              <Text strong style={{ color: "white", fontSize: "18px" }}>
                {currencyFormatPoint(totals.totalNETPAY)} ฿
              </Text>
            </div>
          </div>
        </Card>

        <Card
          size="small"
          style={{
            flex: 1,
            background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
            border: "none",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <Text style={{ color: "white", fontSize: "12px" }}>ยอดเคลียร์</Text>
            <div>
              <Text strong style={{ color: "white", fontSize: "18px" }}>
                {currencyFormatPoint(totals.totalPAY)} ฿
              </Text>
            </div>
          </div>
        </Card>

        <Card
          size="small"
          style={{
            flex: 1,
            background:
              totals.difference === 0
                ? "linear-gradient(135deg, #52c41a 0%, #73d13d 100%)"
                : "linear-gradient(135deg, #eb3349 0%, #f45c43 100%)",
            border: "none",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <Text style={{ color: "white", fontSize: "12px" }}>
              ส่วนต่าง{" "}
              {totals.difference > 0
                ? "(คืน)"
                : totals.difference < 0
                  ? "(เพิ่ม)"
                  : ""}
            </Text>
            <div>
              <Text strong style={{ color: "white", fontSize: "18px" }}>
                {totals.difference > 0 ? "+" : ""}
                {currencyFormatPoint(totals.difference)} ฿
              </Text>
            </div>
          </div>
        </Card>
      </div>

      {/* ============== TABLE ============== */}
      {tableData && tableData.length > 0 ? (
        <>
          <Table
            className="premium-table"
            columns={columns}
            dataSource={tableData}
            rowKey="id"
            pagination={false}
            size="small"
            bordered
            loading={loading}
            onRow={(record) => ({
              className: isLastInGroup(record, tableData)
                ? "group-divider"
                : "",
              style: {
                backgroundColor: getGroupColor(
                  getGroupIndex(record, tableData),
                ),
              },
            })}
            summary={() => {
              const total = tableData.reduce(
                (sum, item) => sum + (parseFloat(item.NETPAY) || 0),
                0,
              );

              return (
                <Table.Summary>
                  <Table.Summary.Row style={{ backgroundColor: "#fafafa" }}>
                    <Table.Summary.Cell index={0} colSpan={3} align="right">
                      <Text strong style={{ fontSize: "16px" }}>
                        ยอดรวมทั้งหมด:
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <Text
                        strong
                        style={{ fontSize: "16px", color: "#1890ff" }}
                      >
                        {currencyFormatPoint(totals.totalNETPAY)}
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <Text
                        strong
                        style={{ fontSize: "16px", color: "#52c41a" }}
                      >
                        {currencyFormatPoint(totals.totalPAY)}
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <Text
                        strong
                        style={{
                          fontSize: "16px",
                          color:
                            totals.difference === 0 ? "#52c41a" : "#ff4d4f",
                        }}
                      >
                        {totals.difference > 0 ? "+" : ""}
                        {currencyFormatPoint(totals.difference)}
                      </Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </>
      ) : (
        <Card size="small" style={{ textAlign: "center", padding: "20px" }}>
          <Text type="secondary">
            {loading ? "กำลังโหลดรายละเอียด..." : "ไม่มีรายการค่าใช้จ่าย"}
          </Text>
        </Card>
      )}

      {/* ============== PAYMENT DETAILS ============== */}
      {/* {tableData && tableData.length > 0 && tableData[0].payDescription && (
        <Card
          size="small"
          title={<Text strong>รายละเอียดการเคลียร์เงิน</Text>}
          style={{ marginTop: 16 }}
        >
          <Descriptions column={2} size="small">
            <Descriptions.Item label="วันที่อนุมัตเคลียร์">
              {tableData[0].PAYDT
                ? convertDateThai(dayjs(tableData[0].PAYDT))
                : "-"}
            </Descriptions.Item>

            {tableData[0].payDescription && (
              <Descriptions.Item label="คำอธิบาย" span={2}>
                <Tag
                  color={
                    totals?.totalNETPAY > totals.totaPAY
                      ? "red"
                      : totals.totalNETPAY < totals.totaPAY
                      ? "red"
                      : "blue"
                  }
                >
                  {totals?.totalNETPAY > totals.totaPAY
                    ? "เบิกเพิ่ม"
                    : totals.totalNETPAY < totals.totaPAY
                    ? "คืนการเงิน"
                    : "ปกติ"}
                </Tag>
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      )} */}

      {/* ============== FORM สำหรับหมายเหตุ ============== */}
      {!dataDefault?.clearStatus && (
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item label={<strong>หมายเหตุการอนุมัติ</strong>} name="remark">
            <TextArea rows={3} placeholder="ระบุหมายเหตุ (ถ้ามี)" />
          </Form.Item>
        </Form>
      )}
      {dataDefault?.clearStatus > 0 && (
        <Card
          size="small"
          style={{
            marginTop: 16,
            background: "#f0f5ff",
            border: "1px solid #adc6ff",
          }}
        >
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Text strong>ข้อมูลการอนุมัติเคลียร์:</Text>
            <Descriptions span={3} size="small">
              <Descriptions.Item label="ผู้อนุมัติ">
                {nameLawyers?.find((item) => item.id === dataDefault?.clearBy)
                  ?.NNAME || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="วันที่อนุมัติเคลียร์">
                {dataDefault.clearDate
                  ? convertDateThai(dayjs(dataDefault.clearDate))
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="สถานะเคลียร์">
                {renderClearStatus(dataDefault?.clearStatus)}
              </Descriptions.Item>
              {dataDefault.smPayMark && (
                <Descriptions.Item label="หมายเหตุ" span={2}>
                  {dataDefault.smPayMark}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Space>
          <Space
            direction="vertical"
            size="small"
            style={{ width: "100%", marginTop: "5px" }}
          >
            <Text strong>ข้อมูลการตั้งเบิก:</Text>
            <Descriptions span={3} size="small">
              <Descriptions.Item label="เลขอ้างอิง">
                {dataDefault?.DOCNO || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="วันที่ตั้ง">
                {dataDefault.DOCDT
                  ? convertDateThai(dayjs(dataDefault.DOCDT))
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="สถานะ">
                <Tag color={dataDefault?.DOCDT ? "green" : "red"}>
                  {" "}
                  {dataDefault?.DOCDT ? "ตั้งเบิกแล้ว" : "ยังไม่ตั้งเบิก"}
                </Tag>
              </Descriptions.Item>
              {dataDefault.smPayMark && (
                <Descriptions.Item label="หมายเหตุ" span={2}>
                  {dataDefault.smPayMark}
                </Descriptions.Item>
              )}
            </Descriptions>
            <Text strong>ข้อมูลการเคลียร์:</Text>
            <Descriptions span={3} size="small">
              <Descriptions.Item label="เลขอ้างอิง">
                {dataDefault?.CL_DOCNO || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="วันที่เคลียร์">
                {dataDefault.CL_DOCDT
                  ? convertDateThai(dayjs(dataDefault.CL_DOCDT))
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="สถานะ">
                <Tag color={dataDefault?.CL_DOCNO ? "green" : "red"}>
                  {" "}
                  {dataDefault?.CL_DOCNO ? "เคลียร์แล้ว" : "รอเคลียร์"}
                </Tag>
              </Descriptions.Item>
              {dataDefault.smPayMark && (
                <Descriptions.Item label="หมายเหตุ" span={2}>
                  {dataDefault.smPayMark}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Space>
        </Card>
      )}

      {bankData &&
        dataDefault?.DOCDT &&
        !dataDefault?.CL_DOCNO &&
        handleModalCreate()}

      {/* ============== FOOTER BUTTONS ============== */}
      <div
        style={{
          marginTop: 24,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Text type="secondary" style={{ fontSize: "12px" }}>
          {dataDefault?.clearStatus === 4
            ? "* กรุณาตรวจสอบข้อมูลก่อนอนุมัติการเคลียร์"
            : "* รายการนี้ดำเนินการแล้ว"}
        </Text>

        <Space size="middle">
          <Button size="large" onClick={handleClose}>
            {dataDefault?.clearStatus === 4 ? "ยกเลิก" : "ปิด"}
          </Button>

          {dataDefault?.clearStatus === 4 &&
            (ROLE_ID === 1 || ROLE_ID === 14) && (
              <Button
                size="large"
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={() => {
                  form.validateFields().then((values) => {
                    handleApprove(values);
                  });
                }}
                loading={loading}
              >
                อนุมัติ
              </Button>
            )}

          {(ROLE_ID === 1 || ROLE_ID === 6) && !dataDefault?.CL_DOCNO && (
            <Popconfirm
              title="ยืนยัน"
              description="คุณต้องการบันทึกใช่หรือไม่ ?"
              onConfirm={handleCreatePayment}
              onCancel={null}
              okText="ยืนยัน"
              cancelText="ยกเลิก"
            >
              <Button
                size="large"
                variant="outlined"
                color="blue"
                onClick={() => {
                  console.log("dataDefault", dataDefault);
                }}
                disabled={
                  !paymentType ||
                  (paymentType !== "1" ? !selectedBank : null) ||
                  dataDefault.CL_DOCNO
                }
              >
                เคลียร์เบิก
              </Button>
            </Popconfirm>
          )}
        </Space>
      </div>
    </Modal>
  );
};

export default ExpenseClearPaymentApproval;
